import { useCallback, useEffect, useRef, useState } from "react"
import equal from "fast-deep-equal"
import Quill from "quill"
import type Delta from "quill-delta"
import {
  applyVisualEdit,
  convertFormatting,
  parseMessageContent,
} from "../markup/conversion"
import { validateMessageContent } from "../markup/validation"
import type {
  CodecResult,
  EditorValidity,
  FormattingTarget,
  MessageContent,
  MessageDiagnostic,
  VisualEdit,
} from "../types"
import {
  deltaToSource,
  deltaToVisualEdit,
  documentToDelta,
  sourceToDelta,
  type QuillPresentation,
} from "./quillAdapter"
import { createTelegramRegistry, installSafeClipboard } from "./slotBlot"
import {
  installEditorHistory,
  editorHistoryOptions,
  textSource,
  type CommandSelection,
  type TextSource,
} from "./editorHistory"
import {
  fromSourceRuns,
  type EditorRange,
  type SourceRun,
} from "./sourceTokens"

export type TelegramEditorView = "visual" | "source"
export interface UseTelegramEditorOptions {
  value: MessageContent
  onChange: (value: MessageContent) => void
  onValidityChange?: (validity: EditorValidity) => void
  readOnly?: boolean
  initialView?: TelegramEditorView
}

interface ViewHistory {
  content: TextSource
  stack: Quill["history"]["stack"]
  selection: EditorRange | null
}

/** Promotion is part of the requested annotation operation, never an emitted
 * intermediate document. Plain typing and no-op open/save retain their codec. */
function applyEditorEdit(content: MessageContent, edit: VisualEdit) {
  const next: MessageContent =
    content.formatting.kind === "plain" &&
    edit.operations.some((operation) => operation.kind === "addEntity")
      ? { ...content, formatting: { kind: "entities", entities: [] } }
      : content
  const parsed = parseMessageContent(next)
  return parsed.ok ? applyVisualEdit(next, edit, parsed.value) : parsed
}

/** Headless controlled editor. Owns only the child it creates in mountRef.
 * `quill` is exposed for user toolbar commands (pass source="user"). Use
 * `applyEdit` for explicit semantic annotation operations; use `convertTo` for
 * format conversion. Merely selecting a view never emits/serializes content. */
export function useTelegramEditor(options: UseTelegramEditorOptions) {
  const latest = useRef(options)
  latest.current = options
  const [element, mountRef] = useState<HTMLElement | null>(null)
  const [quill, setQuill] = useState<Quill | null>(null)
  const quillRef = useRef<Quill | null>(null)
  const [content, setContent] = useState(options.value)
  const accepted = useRef(options.value)
  const [view, setView] = useState<TelegramEditorView>(
    options.initialView ?? "visual",
  )
  const viewRef = useRef(view)
  const [validity, setValidity] = useState(() =>
    validateMessageContent(options.value),
  )
  const [lastGoodPreview, setLastGoodPreview] = useState<MessageContent | null>(
    () => (!validity.previewStale ? options.value : null),
  )
  const [rejected, setRejected] = useState<MessageDiagnostic[]>([])
  const [presentation, setPresentation] = useState<QuillPresentation | null>(
    null,
  )
  const baseline = useRef<QuillPresentation | null>(null)
  const syncing = useRef(false)
  const selection = useRef<EditorRange | null>(null)
  const histories = useRef(new Map<TelegramEditorView, ViewHistory>())
  const history = useRef<ReturnType<typeof installEditorHistory> | null>(null)
  const [availability, setAvailability] = useState({
    canUndo: false,
    canRedo: false,
  })

  const updateState = useCallback((next: MessageContent) => {
    accepted.current = next
    setContent(next)
    const state = validateMessageContent(next)
    setValidity(state)
    setRejected([])
    if (!state.previewStale)
      setLastGoodPreview((previous) =>
        equal(previous, next) ? previous : next,
      )
  }, [])

  const synchronize = useCallback(
    (
      q: Quill,
      next: MessageContent,
      mode: TelegramEditorView,
      patch = false,
    ) => {
      const parsed = mode === "visual" ? documentToDelta(next) : null
      // Invalid visual content is preserved in state, not exposed as a raw
      // source editor. Only the explicit headless source API presents source.
      const nextPresentation = parsed?.ok
        ? parsed.value
        : sourceToDelta(
            mode === "source"
              ? next
              : { ...next, text: "", formatting: { kind: "plain" } },
          )
      const currentDelta = q.getContents()
      syncing.current = true
      // Owned hydration is neither a new edit nor a remote transform. Transform
      // replaces Quill entry identities and would detach their source snapshots.
      const ignoring = q.history.ignoreChange
      q.history.ignoreChange = true
      try {
        if (!equal(currentDelta, nextPresentation.delta)) {
          if (patch) {
            const range = q.getSelection()
            const correction = currentDelta.diff(nextPresentation.delta)
            q.updateContents(correction, "api")
            if (range) {
              const start = correction.transformPosition(range.index)
              const end = correction.transformPosition(
                range.index + range.length,
              )
              q.setSelection(start, end - start, "silent")
            }
          } else q.setContents(nextPresentation.delta, "silent")
        }
        baseline.current = nextPresentation
        setPresentation(nextPresentation)
        q.enable(
          !latest.current.readOnly &&
            (mode === "source" || Boolean(nextPresentation.parsed)),
        )
      } finally {
        q.history.ignoreChange = ignoring
        syncing.current = false
      }
    },
    [],
  )

  const reject = useCallback((diagnostics: MessageDiagnostic[]) => {
    setRejected(diagnostics)
    const q = quillRef.current
    if (!q || !baseline.current) return
    syncing.current = true
    const ignoring = q.history.ignoreChange
    q.history.ignoreChange = true
    try {
      q.setContents(baseline.current.delta, "silent")
      if (selection.current) q.setSelection(selection.current, "silent")
      // Quill already recorded this edit. Keep its pending pre-edit snapshot:
      // finishUserChange restores the stacks and grouping for unchanged source.
      // Owned rollback must not transform their entries or discard cached views.
    } finally {
      q.history.ignoreChange = ignoring
      syncing.current = false
    }
  }, [])

  const accept = useCallback(
    (result: CodecResult<MessageContent>, patch = false) => {
      if (!result.ok) {
        reject(result.diagnostics)
        return result
      }
      const changed = !equal(result.value, accepted.current)
      updateState(result.value)
      const q = quillRef.current
      if (q) synchronize(q, result.value, viewRef.current, patch)
      if (changed) latest.current.onChange(result.value)
      return result
    },
    [reject, synchronize, updateState],
  )

  useEffect(() => {
    if (!element) return
    const host = document.createElement("div")
    element.append(host)
    const q = new Quill(host, {
      registry: createTelegramRegistry(),
      modules: {
        toolbar: false,
        history: editorHistoryOptions,
        uploader: false,
      },
    })
    quillRef.current = q
    setQuill(q)
    const disposeClipboard = installSafeClipboard(q)
    synchronize(q, accepted.current, viewRef.current)
    q.history.clear()
    const viewHistories = histories.current
    const controller = installEditorHistory(
      q,
      () => accepted.current,
      (value) => {
        accept({ ok: true, value }, true)
      },
      setAvailability,
      () => viewHistories.clear(),
    )
    history.current = controller
    const change = (delta: Delta, _old: Delta, source: string) => {
      if (syncing.current) return
      if (source !== "user") {
        // Out-of-band API writes are not controlled content. Restore the
        // authoritative baseline so a subsequent user Delta cannot drift.
        synchronize(q, accepted.current, viewRef.current)
        q.history.clear()
        viewHistories.clear()
        return
      }
      if (latest.current.readOnly || !baseline.current) return
      if (viewRef.current === "source") {
        const raw = deltaToSource(delta, baseline.current)
        accept(
          raw.ok
            ? { ok: true, value: { ...accepted.current, text: raw.value } }
            : raw,
          true,
        )
      } else {
        const edit = deltaToVisualEdit(delta, baseline.current)
        accept(
          edit.ok && baseline.current.parsed
            ? applyEditorEdit(accepted.current, edit.value)
            : (edit as CodecResult<MessageContent>),
          true,
        )
      }
      controller.finishUserChange()
    }
    const editorChange = (
      ...args:
        | ["text-change", Delta, Delta, string]
        | ["selection-change", EditorRange | null, EditorRange | null, string]
    ) => {
      if (args[0] === "text-change") change(args[1], args[2], args[3])
      else {
        if (!syncing.current) selection.current = args[1]
        if (args[1]) q.history.currentRange = args[1]
      }
    }
    // editor-change also sees silent writes. Owned synchronization is guarded;
    // out-of-band writes cannot poison the next user edit's coordinate space.
    q.on("editor-change", editorChange)
    return () => {
      q.off("editor-change", editorChange)
      disposeClipboard()
      controller.dispose()
      history.current = null
      q.disable()
      q.scroll.detach() // Quill 2 has no destroy(); disconnect its MutationObserver.
      q.emitter.removeAllListeners()
      host.remove() // All Quill-owned DOM listeners become unreachable with host.
      quillRef.current = null
      baseline.current = null
      viewHistories.clear()
    }
  }, [element, accept, synchronize])

  useEffect(() => {
    if (equal(options.value, accepted.current)) return // controlled echo, including deep clones
    const textChanged = !equal(
      textSource(options.value),
      textSource(accepted.current),
    )
    updateState(options.value)
    if (textChanged) histories.current.clear()
    const q = quillRef.current
    if (q) {
      synchronize(q, options.value, viewRef.current, !textChanged)
      if (textChanged) q.history.clear() // external text replacement is a new baseline
      q.history.cutoff()
    }
  }, [options.value, synchronize, updateState])

  useEffect(() => {
    if (viewRef.current === view) return
    const q = quillRef.current
    if (q) {
      histories.current.set(viewRef.current, {
        content: textSource(accepted.current),
        stack: {
          undo: [...q.history.stack.undo],
          redo: [...q.history.stack.redo],
        },
        selection: q.getSelection(),
      })
      const saved = histories.current.get(view)
      synchronize(q, accepted.current, view)
      q.history.clear()
      q.history.cutoff()
      if (saved && equal(saved.content, textSource(accepted.current))) {
        // The two views have different coordinate spaces. Keep each undo stack
        // only while its source is unchanged; never replay it in the other view.
        q.history.stack = {
          undo: [...saved.stack.undo],
          redo: [...saved.stack.redo],
        }
        if (saved.selection) q.setSelection(saved.selection, "silent")
      }
      history.current?.refresh()
    }
    viewRef.current = view
  }, [view, synchronize])

  useEffect(() => {
    quillRef.current?.enable(
      !options.readOnly && (view === "source" || Boolean(presentation?.parsed)),
    )
  }, [options.readOnly, view, presentation])

  useEffect(() => {
    latest.current.onValidityChange?.(validity)
  }, [validity, options.onValidityChange])

  const changeSource = useCallback(
    (source: string | readonly SourceRun[]) => {
      if (latest.current.readOnly) return
      const next = {
        ...accepted.current,
        text: typeof source === "string" ? source : fromSourceRuns(source),
      }
      if (equal(next, accepted.current)) return
      accept({ ok: true, value: next })
      quillRef.current?.history.clear() // out-of-band raw field edit, not a Quill user event
    },
    [accept],
  )

  const applyEdit = useCallback(
    (
      edit: VisualEdit,
      selection?: CommandSelection,
    ): CodecResult<MessageContent> | undefined => {
      if (latest.current.readOnly) return
      const parsed = documentToDelta(accepted.current)
      const result = parsed.ok
        ? applyEditorEdit(accepted.current, edit)
        : parsed
      // Prospective validation has not mutated Quill. Unlike rejected typing,
      // it must never roll back the display or discard an existing redo branch.
      if (!result.ok) setRejected(result.diagnostics)
      else if (!equal(result.value, accepted.current)) {
        if (history.current) history.current.command(result.value, selection)
        else accept(result)
      } else setRejected([])
      return result
    },
    [accept],
  )

  const convertTo = useCallback(
    (target: FormattingTarget): CodecResult<MessageContent> | undefined => {
      if (latest.current.readOnly) return
      const result = convertFormatting(accepted.current, target)
      if (!result.ok) {
        setRejected(result.diagnostics)
        return result
      }
      if (equal(result.value, accepted.current)) return result
      accept(result)
      if (result.ok) {
        quillRef.current?.history.clear()
        histories.current.clear()
      }
      return result
    },
    [accept],
  )

  return {
    mountRef,
    quill,
    content,
    view,
    setView,
    presentation,
    validity,
    diagnostics: [...validity.diagnostics, ...rejected],
    rejected,
    lastGoodPreview,
    changeSource,
    applyEdit,
    convertTo,
    canUndo:
      availability.canUndo &&
      !options.readOnly &&
      (view === "source" || Boolean(presentation?.parsed)),
    canRedo:
      availability.canRedo &&
      !options.readOnly &&
      (view === "source" || Boolean(presentation?.parsed)),
  }
}
